/*
 * Meta-data detail page.
 *
 * Mirrors UserDetailPage but for a meta-data slice — a department,
 * location, city, country, company, state or AD group. Lands here when
 * a user drills into a row in the League Table or any other meta-aware
 * widget. Shows the same shape of info as UserDetailPage: header with
 * risk band + score pill, KPI cards (users, sims sent, sims clicked,
 * course completion), group risk-trend chart and susceptibility-by-lure
 * breakdown scoped to events from people in this slice.
 *
 * Calculations:
 *   - everything aggregated from SEND_LOG / COURSE_SEND_LOG filtered
 *     by the meta field (e.dept, e.location, …).
 *   - groupRisk = average of computeBreakdown(email).finalScore across
 *     users in the slice. Falls back to the precomputed risk score on
 *     the dimension aggregate if no events match.
 *   - 6-month trend = monthly average of per-user finalScore.
 *
 * Postgres mapping:
 *   - one query joining `users`, `user_risk_scores` and the
 *     `<meta_field>` filter; aggregate at request time or read from
 *     materialised `<meta>_risk_snapshot` rolling tables.
 *
 * Integration hook: `metaDetailApi.useMetaDetailQuery({ type, value })`
 * returning { totals, breakdown, trend, activity }. Cache 30 min.
 */
import React, { useMemo } from 'react';
import { Box, Grid, Typography, IconButton, Tooltip } from '@mui/material';
import HelpIcon from '@mui/icons-material/HelpOutline';
import { motion } from 'framer-motion';
import MainCard from '../../../components/cards/MainCard';
import CountUp from 'react-countup';
import UserRiskTrendChart from './UserRiskTrendChart';
import UserSusceptibilityCard from './UserSusceptibilityCard';
import { SEND_LOG, COURSE_SEND_LOG, USERS, DEPTS, LOCS, CITIES, COUNTRIES, COMPANIES, STATES, GROUPS } from '../data';
import useDashboardData from '../hooks/useDashboardData';
import { computeBreakdown, bandFor } from '../riskScore';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

const META_FIELD_MAP = {
    department: 'dept',
    location:   'location',
    city:       'city',
    country:    'country',
    company:    'company',
    state:      'state',
    adGroup:    'adGroup',
};

const META_LABEL_MAP = {
    department: 'Department',
    location:   'Location',
    city:       'City',
    country:    'Country',
    company:    'Company',
    state:      'State',
    adGroup:    'AD group',
};

// Getters so each access reads the live hydrated aggregates.
const META_AGG_MAP = {
    get department() { return DEPTS; },
    get location()   { return LOCS; },
    get city()       { return CITIES; },
    get country()    { return COUNTRIES; },
    get company()    { return COMPANIES; },
    get state()      { return STATES; },
    get adGroup()    { return GROUPS; },
};

function MetricCard({ label, value, unit, sub, color }) {
    return (
        <motion.div variants={fadeUp} style={{ height: '100%', width: '100%', display: 'flex' }}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: 0.5 }}>{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: color || 'text.primary' }}>
                            <CountUp end={parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0} duration={1.4} decimals={String(value).includes('.') ? 1 : 0} />
                        </Typography>
                        {unit && <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>{unit}</Typography>}
                    </Box>
                    {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 'auto', pt: 0.5 }}>{sub}</Typography>}
                </Box>
            </MainCard>
        </motion.div>
    );
}

export default function MetaDetailPage({ go, metaType, metaValue, scope }) {
    const field = META_FIELD_MAP[metaType] || 'dept';
    const typeLabel = META_LABEL_MAP[metaType] || 'Group';
    const agg = (META_AGG_MAP[metaType] || []).find((d) => d.name === metaValue);

    // Server slice: leagues row (users + avg score over ALL scored users), sim_dims row
    // (uncapped sends/clicks) and course_dims (uncapped completion) for this value.
    // These rollups don't see the client-pooled ./data, so a Tenancy filter is
    // forwarded to pool the selected child tenancies into the slice.
    const tenancyScope = Array.isArray(scope?.tenancy) && scope.tenancy.length ? { tenancy: scope.tenancy } : undefined;
    const leaguesQ = useDashboardData({ metric: 'simulations', aggregation: 'leagues', scope: tenancyScope });
    const simDimsQ = useDashboardData({ metric: 'simulations', aggregation: 'sim_dims', groupBy: metaType, scope: tenancyScope }, { skip: metaType === 'adGroup' });
    const courseDimsQ = useDashboardData({ metric: 'courses', aggregation: 'course_dims', groupBy: metaType, scope: tenancyScope }, { skip: metaType === 'adGroup' });
    const leagueRow = (leaguesQ.data?.leagues || []).find((r) => r.dimType === metaType && r.name === metaValue);
    const simRow = (simDimsQ.data?.simDims || []).find((r) => r.dimValue === metaValue);
    const courseRows = (courseDimsQ.data?.courseDims || []).filter((r) => r.dimValue === metaValue);
    const courseAgg = courseRows.length ? courseRows.reduce((a, r) => ({ sent: a.sent + r.sent, completed: a.completed + r.completed }), { sent: 0, completed: 0 }) : null;

    // Pull users + events that match this meta slice.
    const usersInSlice = useMemo(() => USERS.filter((u) => u[field] === metaValue), [field, metaValue]);
    const sliceEmails = useMemo(() => usersInSlice.map((u) => u.email), [usersInSlice]);
    const emails = useMemo(() => new Set(sliceEmails), [sliceEmails]);

    const simEvents = useMemo(
        () => SEND_LOG.filter((e) => emails.has(e.email) || e[field] === metaValue),
        [emails, field, metaValue],
    );
    const courseEvents = useMemo(
        () => COURSE_SEND_LOG.filter((e) => emails.has(e.email) || e[field] === metaValue),
        [emails, field, metaValue],
    );

    // Aggregate-first (uncapped); the raw-event computation stays as the fallback.
    const totalUsers = leagueRow?.users ?? (usersInSlice.length || (agg?.users ?? 0));
    const simsSent   = simRow ? simRow.sent : simEvents.length;
    const simsClicked = simRow ? simRow.clicks : simEvents.filter((e) => e.status === 'clicked' || e.status === 'clicked_2nd' || e.status === 'attachment').length;
    const coursesSent = courseAgg ? courseAgg.sent : courseEvents.length;
    const coursesCompleted = courseAgg ? courseAgg.completed : courseEvents.filter((e) => e.dateCompleted).length;
    const courseCompletionPct = coursesSent > 0 ? Math.round((coursesCompleted / coursesSent) * 100) : (agg?.completion ?? 0);

    // Group risk score = mean of per-user finalScore. Falls back to the
    // precomputed aggregate.risk when the breakdown can't be computed
    // for any user in the slice.
    const groupScore = useMemo(() => {
        if (leagueRow?.avgRisk != null) return Math.round(leagueRow.avgRisk);
        const scores = [];
        for (const u of usersInSlice) {
            const b = computeBreakdown(u.email);
            if (b && !b.insufficient) scores.push(b.finalScore);
        }
        if (scores.length === 0) return agg?.risk ?? null;
        return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    }, [usersInSlice, agg, leagueRow]);

    const band = groupScore != null ? bandFor(groupScore) : null;

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

            <motion.div variants={fadeUp}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled' }}>{typeLabel}</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>{metaValue}</Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{totalUsers.toLocaleString()} user{totalUsers === 1 ? '' : 's'}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Risk trend and lure susceptibility for everyone in this {typeLabel.toLowerCase()}.</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {band && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 600, bgcolor: band.bg, color: band.textColor, border: `1px solid ${band.color}` }}>
                                {band.name} risk
                            </Box>
                        )}
                        {groupScore != null && band && (
                            <Tooltip title={`${typeLabel} score is the average of every user's final score in this slice.`}>
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 1,
                                        pl: 1.25, pr: 1.5, py: 0.5,
                                        borderRadius: '8px',
                                        bgcolor: band.bg,
                                        color: band.textColor,
                                        border: `1px solid ${band.color}`,
                                        boxShadow: `0 1px 0 ${band.color}22, 0 3px 10px ${band.color}1a`,
                                    }}
                                >
                                    <Box component="span" sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8 }}>Score</Box>
                                    <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1, color: 'inherit' }}>
                                        <CountUp end={groupScore} duration={1.1} />
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                        <Tooltip title="How is this calculated?">
                            <IconButton size="small" onClick={() => go && go('risk-methodology', 'Risk methodology')} sx={{ p: '2px' }}>
                                <HelpIcon sx={{ color: 'secondary.main', fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </motion.div>

            <Grid container spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
                <Grid item xs={6} md={3}><MetricCard label="Users" value={totalUsers} sub={`In this ${typeLabel.toLowerCase()}`} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Sims Sent" value={simsSent} sub="All time" /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Sims Clicked" value={simsClicked} color={simsClicked > simsSent * 0.2 ? '#FF5252' : simsClicked > 0 ? '#FFCA28' : '#66BB6A'} sub={simsSent > 0 ? `${Math.round((simsClicked / simsSent) * 100)}% of sends` : '-'} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Course Completion" value={courseCompletionPct} unit="%" color={courseCompletionPct >= 80 ? '#66BB6A' : courseCompletionPct >= 50 ? '#FFCA28' : '#FF5252'} sub={`${coursesCompleted} of ${coursesSent} sent`} /></Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: 1 }}>
                <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 50%' }, minWidth: 0 }}>
                    <UserRiskTrendChart emails={sliceEmails} />
                </Box>
                <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 50%' }, minWidth: 0 }}>
                    <UserSusceptibilityCard events={simEvents} />
                </Box>
            </Box>

        </motion.div>
    );
}
